import {CITY_COMMON_BUILDING_TOKENS, KEY_POINT_TOKENS} from "../../../constants/tokens";
import {getCityHexKeys} from "../getCityHexKeys";
import {checkIfCentralQuarter} from "../../buildings/checkIfCentralQuarter";

// Постройки, после которых показывается «город вырос»: обычные строения города.
// Центральный квартал сюда НЕ входит — у него своя ветка (он основывает город
// целиком, см. ниже). Рудники строят только гномы (разведка рудника), но
// перечислять фракцию тут не нужно: жетон на поле уже сам по себе признак
const CITY_GROWTH_TOKENS = [
  CITY_COMMON_BUILDING_TOKENS.QUARTER,
  CITY_COMMON_BUILDING_TOKENS.FARM,
  CITY_COMMON_BUILDING_TOKENS.BARRACKS,
  KEY_POINT_TOKENS.MINE,
  KEY_POINT_TOKENS.SUPER_MINE,
];

// Ключевые точки, доход которых показывается вместе с ростом города
const CITY_KEY_POINT_TOKENS = Object.values(KEY_POINT_TOKENS);

// Запускает ли постройка жетона анимацию роста города. Проверка дешевая — по
// ней вызывающий код решает, стоит ли считать кэши городов (полный обход поля)
export const checkIfCityGrowthToken = (token) => checkIfCentralQuarter(token) || CITY_GROWTH_TOKENS.includes(token);

const getCityKeyPoints = (city, hexes) => {
  return CITY_KEY_POINT_TOKENS.flatMap((tokenType) => (city[tokenType] || [])
    .map((hexKey) => ({hexKey, tokenType: hexes[hexKey] || tokenType})));
};

// Город ищется по ячейке: у города нет собственного id, устойчивый признак —
// его центральный квартал (по нему же города сопоставляются «до/после»)
const findCityByHexKey = (citiesCache, hexKey) => {
  for (const [playerColor, cities] of Object.entries(citiesCache || {})) {
    const city = (cities || []).find((c) => getCityHexKeys(c).has(hexKey));
    if (city) {
      return {playerColor, city};
    }
  }
  return null;
};

const findCityByCentralQuarter = (citiesCache, centralQuarter) => {
  for (const cities of Object.values(citiesCache || {})) {
    const city = (cities || []).find((c) => c.centralQuarter === centralQuarter);
    if (city) {
      return city;
    }
  }
  return null;
};

/**
 * Что подсветить после постройки строения: город, которому досталась ячейка,
 * и ключевые точки, доход которых надо показать вместе с ним.
 *
 * Правила:
 *  - обычные строения города (базовый квартал, ферма, казарма, рудник) —
 *    оверлей по всему городу и доход тех рудников/руин, которые ЭТОЙ постройкой
 *    городу и достались (раньше в нем их не было);
 *  - центральный квартал — оверлей по всему городу и доход ВСЕХ его рудников
 *    и руин: город только что основан, для игрока в нем ново все;
 *  - постройка вне города (или строение не из списка) — ничего.
 *
 * Кэши городов передаются посчитанными заново из hexes «до» и «после»
 * (см. calculateCitiesCache): store-кэш обновляется только на COMPLETE_PHASE и
 * в течение хода строительства не видит только что расширенные города.
 *
 * @param {string} placedHexKey
 * @param {string} placedToken - жетон, который встал на ячейку
 * @param {Object} citiesCacheBefore
 * @param {Object} citiesCacheAfter
 * @param {Object} hexesAfter
 * @returns {{playerColor: string, cityHexKeys: string[],
 *   keyPoints: Array<{hexKey: string, tokenType: string}>}|null}
 */
export const getCityGrowthAnimationTarget = (
  placedHexKey, placedToken, citiesCacheBefore, citiesCacheAfter, hexesAfter
) => {
  if (!checkIfCityGrowthToken(placedToken)) {
    return null;
  }
  const isCentralQuarter = checkIfCentralQuarter(placedToken);

  const found = findCityByHexKey(citiesCacheAfter, placedHexKey);
  if (!found) {
    return null;
  }

  const {playerColor, city} = found;
  const keyPointsAfter = getCityKeyPoints(city, hexesAfter);

  // Центральный квартал: город для игрока новый целиком — показываем доход
  // всех его ключевых точек
  if (isCentralQuarter) {
    return {playerColor, cityHexKeys: [...getCityHexKeys(city)], keyPoints: keyPointsAfter};
  }

  // Прочие строения: только те ключевые точки, которых у этого города раньше
  // не было. Город сопоставляется по центральному кварталу; если города «до»
  // не было вовсе (постройка его и образовала), новыми считаются все
  const cityBefore = findCityByCentralQuarter(citiesCacheBefore, city.centralQuarter);
  const keyPointHexKeysBefore = new Set(
    cityBefore ? getCityKeyPoints(cityBefore, hexesAfter).map(({hexKey}) => hexKey) : []
  );

  return {
    playerColor,
    cityHexKeys: [...getCityHexKeys(city)],
    keyPoints: keyPointsAfter.filter(({hexKey}) => !keyPointHexKeysBefore.has(hexKey)),
  };
};
